import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, CheckCircle, DollarSign, Zap, Edit, Power, Star,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { staggerContainer, staggerItem, fadeInUp, hoverLift } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_AGENTS = [
  { agent_id: "gpt-5.2", name: "GPT-5.2", cost_per_1k_tokens: 0.015, speed_factor: 1.0, provider: "openai" },
  { agent_id: "gpt-4o", name: "GPT-4o", cost_per_1k_tokens: 0.005, speed_factor: 1.2, provider: "openai" },
  { agent_id: "gpt-4o-mini", name: "GPT-4o Mini", cost_per_1k_tokens: 0.0015, speed_factor: 0.8, provider: "openai" },
  { agent_id: "claude-sonnet", name: "Claude Sonnet", cost_per_1k_tokens: 0.003, speed_factor: 1.1, provider: "anthropic" },
  { agent_id: "claude-opus", name: "Claude Opus", cost_per_1k_tokens: 0.015, speed_factor: 1.5, provider: "anthropic" },
  { agent_id: "claude-haiku", name: "Claude Haiku", cost_per_1k_tokens: 0.00025, speed_factor: 0.5, provider: "anthropic" },
  { agent_id: "gemini-3-flash", name: "Gemini 3 Flash", cost_per_1k_tokens: 0.0001, speed_factor: 0.3, provider: "google" },
  { agent_id: "gemini-3-pro", name: "Gemini 3 Pro", cost_per_1k_tokens: 0.00125, speed_factor: 0.9, provider: "google" },
];

export default function CostModelsPanel() {
  const [models, setModels] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formAgents, setFormAgents] = useState([]);

  useEffect(() => { fetchModels(); }, []);

  const fetchModels = async () => {
    try {
      const res = await axios.get(`${API}/agent-models`);
      setModels(res.data);
    } catch { /* ignore */ }
  };

  const openCreate = () => {
    setFormName(""); setFormDesc(""); setFormAgents(DEFAULT_AGENTS.map(a => ({ ...a })));
    setEditingModel(null); setCreateOpen(true);
  };

  const openEdit = (model) => {
    setFormName(model.name); setFormDesc(model.description || "");
    setFormAgents(model.agents.map(a => ({ ...a })));
    setEditingModel(model); setCreateOpen(true);
  };

  const updateAgent = (idx, field, value) => {
    setFormAgents(prev => prev.map((a, i) => i === idx ? { ...a, [field]: parseFloat(value) || value } : a));
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error("Enter a model name"); return; }
    try {
      const payload = { name: formName, description: formDesc, agents: formAgents };
      if (editingModel) {
        await axios.put(`${API}/agent-models/${editingModel.model_id}`, payload);
        toast.success("Cost model updated");
      } else {
        await axios.post(`${API}/agent-models`, payload);
        toast.success("Cost model created");
      }
      setCreateOpen(false); fetchModels();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (modelId) => {
    try {
      await axios.delete(`${API}/agent-models/${modelId}`);
      toast.success("Cost model deleted");
      fetchModels();
    } catch { toast.error("Failed to delete"); }
  };

  const handleActivate = async (modelId) => {
    try {
      await axios.post(`${API}/agent-models/${modelId}/activate`);
      toast.success("Cost model activated");
      fetchModels();
    } catch { toast.error("Failed to activate"); }
  };

  return (
    <motion.div {...fadeInUp} className="space-y-5" data-testid="cost-models-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Custom Cost Models</h3>
          <p className="text-sm text-[var(--qn-foreground-muted)]">Define custom agent pricing for optimizations</p>
        </div>
        <Button onClick={openCreate} size="sm" className="btn-primary" data-testid="create-cost-model-btn">
          <Plus className="w-4 h-4 mr-1" /> New Model
        </Button>
      </div>

      {models.length === 0 && (
        <Card className="card-themed">
          <CardContent className="p-8 text-center text-[var(--qn-foreground-muted)]">
            <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No custom cost models yet. Create one to override default agent pricing.</p>
          </CardContent>
        </Card>
      )}

      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {models.map((model) => (
          <motion.div key={model.model_id} variants={staggerItem} whileHover={hoverLift.whileHover}>
            <Card className={`card-themed ${model.active ? "ring-2 ring-[var(--qn-primary)]" : ""}`} data-testid={`cost-model-${model.model_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    {model.name}
                    {model.active && <Badge className="bg-[var(--qn-primary)]/20 text-[var(--qn-primary)] text-xs"><Star className="w-3 h-3 mr-1" />Active</Badge>}
                  </CardTitle>
                  <div className="flex gap-1">
                    {!model.active && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleActivate(model.model_id)} title="Activate" data-testid={`activate-model-${model.model_id}`}>
                        <Power className="w-3.5 h-3.5 text-green-400" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(model)}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => handleDelete(model.model_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {model.description && <p className="text-xs text-[var(--qn-foreground-muted)]">{model.description}</p>}
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {(model.agents || []).slice(0, 4).map((a) => (
                    <div key={a.agent_id} className="flex items-center justify-between text-sm">
                      <span className="text-[var(--qn-foreground-muted)]">{a.name}</span>
                      <span className="font-mono">${a.cost_per_1k_tokens}/1k</span>
                    </div>
                  ))}
                  {(model.agents || []).length > 4 && (
                    <p className="text-xs text-[var(--qn-foreground-subtle)]">+{model.agents.length - 4} more agents</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingModel ? "Edit" : "Create"} Cost Model</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Name</label>
              <Input value={formName} onChange={e => setFormName(e.target.value)}
                className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="cost-model-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description</label>
              <Input value={formDesc} onChange={e => setFormDesc(e.target.value)}
                className="bg-[var(--qn-background)] border-[var(--qn-border)]" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Agent Pricing</label>
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                {formAgents.map((a, i) => (
                  <div key={a.agent_id} className="grid grid-cols-[1fr_80px_70px] gap-2 items-center text-sm bg-[var(--qn-background)] rounded-lg p-2">
                    <span className="font-medium">{a.name}</span>
                    <Input type="number" step="0.0001" value={a.cost_per_1k_tokens}
                      onChange={e => updateAgent(i, "cost_per_1k_tokens", e.target.value)}
                      className="h-7 text-xs bg-transparent border-[var(--qn-border)]" />
                    <Input type="number" step="0.1" value={a.speed_factor}
                      onChange={e => updateAgent(i, "speed_factor", e.target.value)}
                      className="h-7 text-xs bg-transparent border-[var(--qn-border)]" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-[var(--qn-foreground-subtle)] mt-1">Cost per 1K tokens | Speed factor (1.0 = baseline)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="save-cost-model-btn">
              {editingModel ? "Update" : "Create"} Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
